/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React from 'react';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import FilterIcon from '@atlaskit/icon/core/filter';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import type { SingleValue } from '@atlaskit/react-select/types';
import AsyncSelect from '@atlaskit/select/async-select';
import EnterEscapeHandler from '@jira/platform__enter-escape-handler/src/index.tsx';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { PICKER_SEARCH_DEBOUNCE_TIME } from './boardTypes.constants.tsx';
import type {
	FieldValueSinglePickerProps,
	GenericPickerOption,
} from './BoardCreateFormValues.types.tsx';
import { useLoadSavedFiltersController } from './useLoadSavedFiltersController.tsx';

const savedFilterPickerPlaceholder = defineMessage({
	id: 'software-board-create.modal.form.source-value-picker.saved-filter-picker.saved-filter-picker-placeholder',
	defaultMessage: 'Select',
	description:
		'Placeholder text shown in the saved filter picker dropdown before a selection is made',
});

export type SavedFilterPickerProps = {
	label: string;
} & FieldValueSinglePickerProps<SingleValue<GenericPickerOption>>;

const formatOptionLabel = (option: GenericPickerOption) => (
	<Flex alignItems="center" gap="space.100">
		<FilterIcon spacing="spacious" label={option.label} />
		{option.label}
	</Flex>
);

export const SavedFilterPicker = ({
	onChange,
	value,
	isInvalid,
	label,
}: SavedFilterPickerProps): React.JSX.Element => {
	const { loadOptions, isLoading } = useLoadSavedFiltersController();
	const { formatMessage } = useIntl();

	const debouncedLoadOptions = debounce(
		(searchString, callback) => loadOptions(searchString).then((options) => callback(options)),
		PICKER_SEARCH_DEBOUNCE_TIME,
	);

	return (
		/* Prevent ESC press on Select bubbling up to the modal */
		<EnterEscapeHandler onEscape={noop}>
			<AsyncSelect
				aria-label={label}
				isLoading={isLoading}
				formatOptionLabel={formatOptionLabel}
				defaultOptions
				loadOptions={debouncedLoadOptions}
				onChange={onChange}
				value={value}
				placeholder={formatMessage(savedFilterPickerPlaceholder)}
				menuPosition="fixed"
				classNamePrefix="source-value-picker"
				isInvalid={isInvalid}
			/>
		</EnterEscapeHandler>
	);
};
